<template>
  <div v-if="!isNativeApp" class="store-badges">
    <div class="badge-links">
      <a v-if="iosLive" :href="appStoreUrl" target="_blank" rel="noopener">
        <img
          :src="appStoreBadge"
          :alt="$t('storeBadges.appStoreAlt')"
          class="store-badge"
        />
      </a>
      <a :href="playStoreHref()" target="_blank" rel="noopener">
        <img
          :src="playBadge"
          :alt="$t('storeBadges.googlePlayAlt')"
          class="store-badge"
        />
      </a>
    </div>
    <p v-if="!iosLive" class="ios-coming-soon">
      {{ $t('storeBadges.iosComingSoon') }}
    </p>
  </div>
</template>

<script>
import {
  APP_STORE_URL,
  PLAY_STORE_URL,
  IOS_LIVE,
} from "@/constants/appStoreLinks.js";
import { storeBadgesStore } from "@/store/storeBadges.js";
import { readAcquisitionForSeed } from "@/scripts/acquisition.js";

// The referrer a badge tap sends when this browser has no stored first touch.
// Without it an untagged website visitor who installs through the badge would
// land in Google's organic bucket, mixed in with Play-search discovery.
const WEB_REFERRAL = "utm_source=articulearnai-web&utm_medium=referral";

// The inner `key=value&key=value` string Play hands back to the installed app
// through the Install Referrer API. `al_landed` carries the web landing time
// so the Android reader can keep it instead of using the install time. Every
// token here has already passed the rule's [a-z0-9-] check in
// readAcquisitionForSeed, and al_landed is included only as a plain integer,
// because the Android parser accepts nothing but digits for it.
function buildPlayReferrer(record) {
  if (!record) return WEB_REFERRAL;
  const pairs = [`utm_source=${record.source}`];
  if (record.medium) pairs.push(`utm_medium=${record.medium}`);
  if (record.campaign) pairs.push(`utm_campaign=${record.campaign}`);
  if (Number.isSafeInteger(record.landedAt)) {
    pairs.push(`al_landed=${record.landedAt}`);
  }
  return pairs.join("&");
}

export default {
  name: "StoreBadges",
  data() {
    return {
      appStoreUrl: APP_STORE_URL,
      // Apple is still reviewing the iOS listing; until this flips, the App
      // Store badge would link to a page that 404s.
      iosLive: IOS_LIVE,
      // Store badges must never render inside the native shells: Apple
      // rejects iOS builds that link to Google Play.
      isNativeApp: window.Capacitor?.isNativePlatform?.() === true,
    };
  },
  computed: {
    appStoreBadge() {
      return storeBadgesStore.getAppStoreBadge(this.$i18n.locale);
    },
    playBadge() {
      return storeBadgesStore.getPlayBadge(this.$i18n.locale);
    },
  },
  methods: {
    // A method rather than a computed property on purpose: localStorage is not
    // reactive, so a computed value would be cached forever after the first
    // render. A method is re-read on every render (a locale change, for
    // example). Nothing re-renders the badge when a stored touch expires, so a
    // page left open keeps the href from its last render until something else
    // causes the next one.
    //
    // The inner string is percent-encoded exactly once, which is what keeps
    // its second and later pairs inside the referrer value instead of turning
    // them into sibling query parameters that Play would drop. Any failure
    // falls back to the plain listing URL, so the badge always works.
    playStoreHref() {
      try {
        const referrer = buildPlayReferrer(readAcquisitionForSeed(Date.now()));
        return `${PLAY_STORE_URL}&referrer=${encodeURIComponent(referrer)}`;
      } catch {
        return PLAY_STORE_URL;
      }
    },
  },
};
</script>

<style scoped>
.store-badges {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 8px;
}

.badge-links {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 12px;
  flex-wrap: wrap;
}

.store-badge {
  height: 44px;
  display: block;
  transition: opacity 0.3s ease;
}

/* On a touch WebView :hover latches onto the last-tapped element until the
   next tap elsewhere, so without this guard a tapped badge stayed dimmed to
   0.8 for as long as the page was open, both here and under FinalCtaSection. */
@media (hover: hover) and (pointer: fine) {
  .store-badge:hover {
    opacity: 0.8;
  }
}

.ios-coming-soon {
  margin: 0;
  font-size: 0.875rem;
  color: var(--al-text-muted);
}

@media (max-width: 767px) {
  .store-badge {
    height: 40px;
  }
}
</style>
